import { Pipe, PipeTransform } from '@angular/core';
import { GableSuggestionCardPayload } from '../components/gable/gable-suggestion-card/gable-suggestion-card.component';

export interface GableSuggestionSplit {
    /** Message markdown with all gable-suggestions blocks removed. */
    text: string;
    /** Parsed card payloads, in document order. */
    cards: GableSuggestionCardPayload[];
}

/**
 * Splits a Gable assistant message into prose + rich suggestion cards.
 *
 * The box emits recommendation sets as fenced code blocks tagged
 * `gable-suggestions` containing JSON (see gable-box orchestrator
 * system-prompt "Suggested-decisions card"). This pipe strips those blocks
 * out of the markdown (so the raw JSON never renders) and returns the parsed
 * payloads for `app-gable-suggestion-card`.
 *
 * Streaming: an OPENED-but-unclosed block (fence started, no closing ```)
 * is stripped to the end of the string and yields no card — the user never
 * sees partial JSON; the card pops in whole once the fence closes.
 *
 * Pure pipe — memoized per bound message content, so parsing doesn't rerun
 * on every change-detection tick.
 */
@Pipe({
    name: 'gableSuggestions',
    standalone: true,
    pure: true,
})
export class GableSuggestionsPipe implements PipeTransform {

    private static readonly CLOSED_BLOCK = /```gable-suggestions\s*\n([\s\S]*?)```/g;
    private static readonly OPEN_BLOCK = /```gable-suggestions[\s\S]*$/;

    transform(content: string | null | undefined): GableSuggestionSplit {
        const raw = content ?? '';
        if (!raw.includes('```gable-suggestions')) {
            return { text: raw, cards: [] };
        }

        const cards: GableSuggestionCardPayload[] = [];
        let text = raw.replace(GableSuggestionsPipe.CLOSED_BLOCK, (_match, json: string) => {
            const parsed = this.parsePayload(json);
            if (parsed) {
                cards.push(parsed);
            }
            return '';
        });

        // Still-streaming block: hide the partial JSON tail.
        text = text.replace(GableSuggestionsPipe.OPEN_BLOCK, '');

        return { text: text.trim(), cards };
    }

    private parsePayload(json: string): GableSuggestionCardPayload | null {
        try {
            const value = JSON.parse(json);
            if (!value || typeof value !== 'object' || !Array.isArray(value.items)) {
                return null;
            }
            return value as GableSuggestionCardPayload;
        } catch {
            // Malformed JSON from the model — drop the card rather than
            // breaking the message render.
            return null;
        }
    }
}
